const BAIT_SETTLE_MS = 250;
const AD_REQUEST_PROBE_URL = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
const AD_REQUEST_PROBE_TIMEOUT_MS = 2000;

const wait = (milliseconds: number): Promise<void> =>
  new Promise(resolve => {
    window.setTimeout(resolve, milliseconds);
  });

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

async function detectHiddenAdElement(): Promise<boolean> {
  const bait = document.createElement('div');

  bait.id = 'ad-banner';
  bait.className = 'adsbox adsbygoogle ad-banner ad-unit ad-zone ad-placement';
  bait.setAttribute('aria-hidden', 'true');
  bait.innerHTML = '&nbsp;';

  // Keep the bait inside the layout box. On mobile, html/body are
  // height: 100% with overflow-x: hidden, and WebKit reports 0×0 (and a null
  // offsetParent) for an element parked at a large negative offset. That is
  // the same signal cosmetic filters produce, so it was a false positive.
  Object.assign(bait.style, {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '1px',
    height: '1px',
    display: 'block',
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: '-1',
  });

  document.body.appendChild(bait);

  await new Promise<void>(resolve => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve());
    });
  });
  await wait(BAIT_SETTLE_MS);

  const computedStyle = window.getComputedStyle(bait);
  const opacity = Number(computedStyle.opacity);

  const isBlocked =
    !bait.isConnected ||
    bait.offsetWidth === 0 ||
    bait.offsetHeight === 0 ||
    computedStyle.display === 'none' ||
    computedStyle.visibility === 'hidden' ||
    opacity === 0;

  bait.remove();

  return isBlocked;
}

type AdsByGoogle = {
  loaded?: boolean;
  push?: (...args: unknown[]) => unknown;
};

type AdScriptProbeStatus = 'ok' | 'blocked' | 'redirected' | 'timeout';

/**
 * Redirect rules answer with a stand-in (`noop.js`, a GPT surrogate) so the
 * script `load` event still fires. A real adsbygoogle.js replaces `push`
 * before that event; an empty or stub redirect does not.
 */
function isRealAdsByGoogle(value: AdsByGoogle | undefined): boolean {
  if (!value || typeof value.push !== 'function') {
    return false;
  }

  const source = Function.prototype.toString.call(value.push);

  if (value.loaded === true) {
    return true;
  }

  return !source.includes('[native code]');
}

function probeAdScript(): Promise<AdScriptProbeStatus> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return Promise.resolve('ok');
  }

  return new Promise(resolve => {
    const script = document.createElement('script');
    let settled = false;

    const finish = (status: AdScriptProbeStatus) => {
      if (settled) {
        return;
      }

      settled = true;
      window.clearTimeout(timeoutId);
      script.onload = null;
      script.onerror = null;
      script.remove();
      resolve(status);
    };

    const timeoutId = window.setTimeout(() => finish('timeout'), AD_REQUEST_PROBE_TIMEOUT_MS);

    script.async = true;
    script.src = AD_REQUEST_PROBE_URL;
    script.onload = () => {
      const ads = (window as Window & { adsbygoogle?: AdsByGoogle }).adsbygoogle;
      finish(isRealAdsByGoogle(ads) ? 'ok' : 'redirected');
    };
    script.onerror = () => finish('blocked');
    document.head.appendChild(script);
  });
}

/**
 * Network blocks reject the request. Redirect extensions return a stand-in
 * body, so the request succeeds and the script probe has to notice the missing API.
 * An abort is a slow network and is not treated as a blocker.
 */
async function isAdRequestBlocked(): Promise<boolean> {
  const [fetchBlocked, scriptStatus] = await Promise.all([probeAdFetch(), probeAdScript()]);

  return fetchBlocked || scriptStatus === 'blocked' || scriptStatus === 'redirected';
}

async function probeAdFetch(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), AD_REQUEST_PROBE_TIMEOUT_MS);

  try {
    await fetch(AD_REQUEST_PROBE_URL, {
      method: 'GET',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });

    return false;
  } catch (error) {
    if (isAbortError(error)) {
      return false;
    }

    return true;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export type GptStatus = 'loaded' | 'failed' | 'timeout' | 'redirected';

/**
 * Surrogate scripts used by redirect rules set `apiReady` but `getVersion()`
 * is empty. The real GPT script returns a numeric build id.
 */
function isGptSurrogate(): boolean {
  const gpt = window.googletag;

  if (!gpt || typeof gpt.getVersion !== 'function') {
    return true;
  }

  try {
    const version = gpt.getVersion();

    return typeof version !== 'string' || version.trim().length < 8;
  } catch {
    return true;
  }
}

function hasGptBootstrap(): boolean {
  return Array.isArray(window.googletag?.cmd);
}

/**
 * gpt.js is only a loader. Its `load` event fires before `pubads_impl` arrives
 * and sets `apiReady`. On a phone that second script often takes well over
 * 150ms, so treating "loaded but not apiReady yet" as a redirect blocked
 * everyone without an ad blocker. An empty redirect never installs `cmd`.
 */
async function waitForGpt(timeoutMilliseconds = 2500): Promise<GptStatus> {
  const deadline = Date.now() + timeoutMilliseconds;

  const status = (): GptStatus | null => {
    if (window.__gptLoadState === 'failed') {
      return 'failed';
    }

    if (window.googletag?.apiReady) {
      return isGptSurrogate() ? 'redirected' : 'loaded';
    }

    if (window.__gptLoadState === 'loaded' && !hasGptBootstrap()) {
      return 'redirected';
    }

    return null;
  };

  while (Date.now() < deadline) {
    const current = status();

    if (current) {
      return current;
    }

    await wait(100);
  }

  return status() ?? 'timeout';
}

export interface AdBlockDetectionResult {
  blocked: boolean;
  baitBlocked: boolean;
  requestBlocked: boolean;
  gptStatus: GptStatus;
}

export async function detectAdBlock(): Promise<AdBlockDetectionResult> {
  const [baitBlocked, requestBlocked, gptStatus] = await Promise.all([
    detectHiddenAdElement(),
    isAdRequestBlocked(),
    waitForGpt(2500),
  ]);

  return {
    blocked: baitBlocked || requestBlocked || gptStatus === 'failed' || gptStatus === 'redirected',
    baitBlocked,
    requestBlocked,
    gptStatus,
  };
}

export const AD_BLOCK_DETECTION_TIMEOUT_MS = 3500;
